<template>
  <form
    id="editProfileForm"
    class="flex flex-col gap-4"
    novalidate
    @submit.prevent="handleSubmit"
  >
    <BaseFormErrorSummary
      :errors="errors"
      :submit-count="submitCount"
    />
    
    <BaseCard title="Account Information">
      <div class="grid gap-4 md:grid-cols-6">
        <BaseTextInput
          id="firstName"
          v-model="values.firstName"
          class="md:col-span-2"
          disabled
          label="First Name"
          name="firstName"
          required
        />

        <BaseTextInput
          id="middleName"
          v-model="values.middleName"
          autocomplete="additional-name"
          class="md:col-span-2"
          label="Middle Name"
          maxlength="50"
          name="middleName"
        />

        <BaseTextInput
          id="lastName"
          v-model="values.lastName"
          class="md:col-span-2"
          disabled
          label="Last Name"
          name="lastName"
          required
        />

        <BaseTextInput
          id="email"
          v-model="values.email"
          autocomplete="email"
          class="md:col-span-3"
          label="Personal Email"
          name="email"
          required
          disabled
          type="email"
          validation-rules="required|email"
        />

        <BaseTextInput
          id="secondaryEmail"
          v-model="values.secondaryEmail"
          autocomplete="email"
          class="md:col-span-3"
          label="Secondary Email"
          name="secondaryEmail"
          type="email"
          validation-rules="email"
        />
      </div>
    </BaseCard>

    <BaseCard title="Personal Information">
      <div class="grid gap-4 md:grid-cols-12">
        <BaseCountrySelect
          v-model="values.country"
          class="md:col-span-4"
          label="Country"
        />

        <BaseTextInput
          id="address"
          v-model="values.address"
          autocomplete="street-address"
          class="md:col-span-4"
          helper-text-id="street-address-helper-text"
          label="Address"
          name="address"
          required
          validation-rules="required"
        >
          <template #helperText>
            <p v-text="'Home address required for CE credit'" />
          </template>
        </BaseTextInput>

        <BaseTextInput
          id="addressTwo"
          v-model="values.addressTwo"
          class="md:col-span-4"
          label="Address Line 2"
          name="addressTwo"
        />

        <BaseTextInput
          id="city"
          v-model="values.city"
          class="md:col-span-4"
          label="City"
          name="city"
          required
          validation-rules="required"
        />

        <BaseStateSelect
          v-model="values.state"
          :country-id="values.country"
          class="md:col-span-4"
          label="State or Province"
          :required="hasStatesForHome"
          :validation-rules="hasStatesForHome ? 'required' : ''"
          @regions-change="onHomeRegionsChange"
          @clear-state="setFieldValue('state', '')"
        />

        <BaseTextInput
          id="postcode"
          v-model="values.postcode"
          v-maska="'#####'"
          autocomplete="postal-code"
          class="md:col-span-4"
          inputmode="numeric"
          label="Zip Code"
          name="postcode"
          required
          validation-rules="required"
        />

        <BaseTextInput
          id="phoneNumber"
          v-model="values.phoneNumber"
          v-maska="'(###) ###-####'"
          class="md:col-span-6 lg:col-span-3"
          label="Personal Mobile Phone"
          name="phoneNumber"
          required
          type="tel"
          validation-rules="required"
        />

        <BaseTextInput
          id="secondaryPhoneNumber"
          v-model="values.secondaryPhoneNumber"
          v-maska="'(###) ###-####'"
          class="md:col-span-6 lg:col-span-3"
          label="Secondary Phone"
          name="secondaryPhoneNumber"
          type="tel"
        />

        <BaseSelectInput
          id="phoneType"
          v-model="values.phoneType"
          class="md:col-span-6 lg:col-span-3"
          default-option-text="Please select one"
          label="Type of Phone"
          name="phoneType"
        >
          <option
            v-for="phoneType in phoneTypes"
            :key="phoneType.value"
            :value="phoneType.value"
            v-text="phoneType.label"
          />
        </BaseSelectInput>

        <BaseTextInput
          id="phoneExtension"
          v-model="values.phoneExtension"
          class="md:col-span-6 lg:col-span-3"
          label="Extension"
          name="phoneExtension"
        />
      </div>
    </BaseCard>

    <BaseCard title="Professional Information">
      <div class="grid gap-x-4 gap-y-6 md:grid-cols-2">
        <BaseSelectInput
          id="primaryPlaceOfEmployment"
          v-model="values.primaryPlaceOfEmployment"
          default-option-text="Make a selection"
          label="Primary Place of Employment"
          name="primaryPlaceOfEmployment"
          required
          validation-rules="required"
        >
          <option
            v-for="placeOfEmployment in placeOfEmployments"
            :key="placeOfEmployment.value"
            :value="placeOfEmployment.value"
            v-text="placeOfEmployment.label"
          />
        </BaseSelectInput>

        <BaseTextInput
          id="currentEmployerName"
          v-model="values.currentEmployerName"
          label="Current Employer Name"
          name="currentEmployerName"
          required
          validation-rules="required"
        />

        <BaseTextInput
          id="currentEmployerCity"
          v-model="values.currentEmployerCity"
          label="Current Employer City"
          name="currentEmployerCity"
          required
          validation-rules="required"
        />

        <BaseStateSelect
          id="currentEmployerState"
          v-model="values.currentEmployerState"
          :country-id="values.country"
          label="Current Employer State"
          name="currentEmployerState"
          :required="hasStatesForEmployer"
          :validation-rules="hasStatesForEmployer ? 'required' : ''"
          @regions-change="onEmployerRegionsChange"
          @clear-state="setFieldValue('currentEmployerState', '')"
        />

        <fieldset class="flex flex-col gap-y-4 md:col-span-full">
          <legend class="mb-3 text-sm font-semibold text-gray-800">
            <span v-text="'Which best describes your management level in your organization?'" />
            <span
              aria-hidden="true"
              class="ml-1 text-red"
              v-text="'*'"
            />
          </legend>

          <BaseRadioInput
            v-for="managementLevel in managementLevels"
            :id="managementLevel.value"
            :key="managementLevel.value"
            v-model="values.managementLevel"
            :input-value="managementLevel.value"
            :label="managementLevel.label"
            name="managementLevel"
            validation-rules="required"
          />
        </fieldset>

        <fieldset class="md:col-span-full">
          <legend
            class="mb-3 text-sm font-semibold text-gray-800"
            v-text="'Current or Past WC Certifications'"
          />

          <div class="grid grid-flow-col grid-rows-4 gap-x-6 gap-y-4 md:justify-start">
            <BaseCheckboxInput
              v-for="certification in WCCertifications"
              :id="certification"
              :key="certification"
              v-model="values.wcCertificates"
              :input-value="certification"
              :label="certification"
              name="wcCertificates"
            />
          </div>
        </fieldset>
      </div>
    </BaseCard>

    <BaseCard title="Professional Licenses">
      <EditLicensesTable
        v-if="localLicenses.length > 0"
        :customer-id="customerId"
        :licenses="localLicenses"
        @edit-license="selectedLicense = $event"
        @license-deleted="handleLicenseDeleted"
      />

      <LicenseForm
        :country-id="values.country"
        :customer-id="customerId"
        :license="selectedLicense"
        @license-added="handleLicenseAdded"
        @license-deleted="handleLicenseDeleted"
        @clearState="selectedLicense = null"
      />
    </BaseCard>

    <div class="flex justify-end gap-x-2 max-lg:px-4">
      <button
        :disabled="isLoading"
        class="btn btn--primary max-md:flex-1"
        type="submit"
      >
        <span class="relative inline-flex items-center justify-center">
          <BaseLoadingIcon :visible="isLoading" />
          <span
            class="whitespace-nowrap"
            :class="{ 'invisible': isLoading }"
            v-text="'Save Changes'"
          />
        </span>
      </button>

      <button
        :disabled="isSubmitting"
        class="btn btn--outline max-md:flex-1"
        data-test-id="cancel-edit-button"
        type="button"
        @click="$emit('cancelEdit')"
        v-text="'Cancel'"
      />
    </div>
  </form>
</template>

<script lang="ts" setup>
import { vMaska } from 'maska/vue'
import { computed, ref } from 'vue'
import { useForm } from 'vee-validate'

import BaseCard from '@js/components/global/BaseCard.vue'
import LicenseForm from '@js/components/account/profile/LicenseForm.vue'
import BaseStateSelect from '@js/components/global/forms/BaseStateSelect.vue'
import BaseTextInput from '@js/components/global/forms/inputs/BaseTextInput.vue'
import BaseCountrySelect from '@js/components/global/forms/BaseCountrySelect.vue'
import BaseRadioInput from '@js/components/global/forms/inputs/BaseRadioInput.vue'
import BaseSelectInput from '@js/components/global/forms/inputs/BaseSelectInput.vue'
import EditLicensesTable from '@js/components/account/profile/EditLicensesTable.vue'
import BaseFormErrorSummary from '@js/components/global/forms/BaseFormErrorSummary.vue'
import BaseCheckboxInput from '@js/components/global/forms/inputs/BaseCheckboxInput.vue'
import BaseLoadingIcon from '@js/components/global/BaseLoadingIcon.vue'

import { log } from '@js/utils/logger'
import { updateCustomerProfile } from '@js/api/customer/updateCustomerProfile'
import { managementLevels, phoneTypes, placeOfEmployments, WCCertifications } from '@js/utils/form'

import { CustomerLicense } from '@js/api/types/customer/CustomerLicense'
import { CustomerProfile } from '@js/api/types/customer/CustomerProfile'

defineOptions({
  name: 'EditProfileForm',
})

const props = defineProps<{
  customerId: number
  licenses: CustomerLicense[]
  profile: CustomerProfile | null
}>()

const emit = defineEmits<{
  cancelEdit: [void]
  updateIsSubmitting: [boolean]
  licenseDeleted: [licenseId: number]
  saveChanges: [updatedProfile: CustomerProfile | null]
}>()

const {
  errors,
  values,
  resetForm,
  submitCount,
  isSubmitting,
  setFieldValue,
  handleSubmit: handleValidationSubmit,
} = useForm({
  initialValues: {
    firstName: props.profile?.first_name || '',
    middleName: props.profile?.middle_name || '',
    lastName: props.profile?.last_name || '',
    email: props.profile?.email || '',
    secondaryEmail: props.profile?.secondary_email || '',
    country: props.profile?.personal.country || 'US',
    address: props.profile?.personal.address || '',
    addressTwo: props.profile?.personal.address_two || '',
    city: props.profile?.personal.city || '',
    state: props.profile?.personal.state || '',
    postcode: props.profile?.personal.postcode || '',
    phoneNumber: props.profile?.personal.phone || props.profile?.customer_contact_number || '',
    secondaryPhoneNumber: props.profile?.personal.secondary_phone || '',
    phoneType: props.profile?.personal.phone_type || '',
    phoneExtension: props.profile?.personal.phone_ext || '',
    primaryPlaceOfEmployment: props.profile?.professional.primary_employment || '',
    currentEmployerName: props.profile?.professional.employer_name || '',
    currentEmployerCity: props.profile?.professional.employer_city || '',
    currentEmployerState: props?.profile?.professional.employer_state || '',
    managementLevel: props.profile?.professional.management_level || '',
    wcCertificates: props.profile?.professional.wc_certificates || [],
  },
})

const homeRegionCount = ref(0)
const employerRegionCount = ref(0)
const hasStatesForHome = computed(() => homeRegionCount.value > 0)
const hasStatesForEmployer = computed(() => employerRegionCount.value > 0)

const selectedLicense = ref<CustomerLicense | null>(null)
const localLicenses = computed(() => props.licenses)
const isLoading = ref(false)

/**
 * Handle when the list of regions changes for the home address state select
 */
const onHomeRegionsChange = (list: Array<{label: string; value: string}>) => {
  homeRegionCount.value = Array.isArray(list) ? list.length : 0
  if (homeRegionCount.value === 0) setFieldValue('state', '')
}

/**
 * Handle when the list of regions changes for the employer address state select
 */
const onEmployerRegionsChange = (list: Array<{label: string; value: string}>) => {
  employerRegionCount.value = Array.isArray(list) ? list.length : 0
  if (employerRegionCount.value === 0) setFieldValue('currentEmployerState', '')
}

const handleSubmit = handleValidationSubmit(async (): Promise<void> => {
  isLoading.value = true
  try {
    emit('updateIsSubmitting', true)

    const updatedProfile = await updateCustomerProfile({
      customerId: props.profile?.customer_id as number,
      firstName: values.firstName,
      middleName: values.middleName,
      lastName: values.lastName,
      email: props.profile?.email as string,
      secondaryEmail: values.secondaryEmail,
      customerContactNumber: values.phoneNumber,
      personal: {
        country: values.country,
        address: values.address,
        addressTwo: values.addressTwo,
        city: values.city,
        state: values.state,
        postcode: values.postcode,
        phone: values.phoneNumber,
        secondaryPhone: values.secondaryPhoneNumber,
        phoneType: values.phoneType,
        phoneExt: values.phoneExtension,
      },
      professional: {
        primaryPlaceOfEmployment: values.primaryPlaceOfEmployment,
        primaryEmployerName: values.currentEmployerName,
        primaryEmployerCity: values.currentEmployerCity,
        primaryEmployerState: values.currentEmployerState,
        managementLevel: values.managementLevel,
        wcCertificates: values.wcCertificates,
      },
    })

    emit('saveChanges', updatedProfile)
    emit('updateIsSubmitting', false)
  } catch (error) {
    log({
      message: 'Profile Update Error',
      file: 'src/wcei/js/components/account/profile/EditProfileForm.vue',
    })
  } finally {
    resetForm()
    isLoading.value = false
  }
})

const handleLicenseAdded = (license: CustomerLicense): void => {
  const existingLicenseIndex = localLicenses.value.findIndex(
    localLicense => localLicense.license_id === license.license_id,
  )

  if (existingLicenseIndex > -1) {
    localLicenses.value.splice(existingLicenseIndex, 1, license)
  } else {
    localLicenses.value.push(license)
  }
}

const handleLicenseDeleted = (licenseId: number): void => {
  emit('licenseDeleted', licenseId)

  selectedLicense.value = null
}
</script>
